import { Injector } from '@angular/core';
import {
  PageComponentService,
  DefaultPageComponentType,
  isPageLayoutType,
  PageLayoutType,
} from 'src/app/core/interfaces/page-creator';
import { PAGE_DATA } from 'src/types/tokens/inject-tokens';
import { DefaultPageComponentService } from '../services/layout/default-page-component.service';
import { HeroBannerPageComponentService } from '../services/layout/hero-banner-page-component.service';
import { PageLayoutComponentService } from '../services/layout/page-layout-component.service';
import { HeroBannerPageComponentType, isHeroBannerPageComponentType } from 'src/app/core/interfaces/page-creator/hero-banner';

export function createInjector(injector: Injector, data: any): Injector {
  const providers = [];
  if (data) {
    providers.push({ provide: PAGE_DATA, useValue: data });
  }
  return Injector.create({
    parent: injector,
    providers: providers,
  });
}

/**
 * Generates a unique ID for a component by appending a random alphanumeric string to the component ID.
 *
 * It is common to use Builder page components with same ID in some pages created dynamically.
 * Use this function to ensure each component has a unique ID and track it in the template.
 * This way, you will avoid NG0955 errors.
 * https://angular.dev/errors/NG0955
 * @param componentId The ID of the component.
 * @returns A unique ID for the component.
 */
export function generateUniqueComponentId(componentId: string): string {
  const randomId = Math.random().toString(36).substring(2, 7); // Generate a random alphanumeric string of 5 characters
  return `${componentId}-${randomId}`;
}

/**
 * Factory method to create an instance of the appropriate PageComponentService based on the Page Component and Layout type.
 * This method helps in loading custom Page Component Services lazily, improving the performance.
 *
 * @param injector - The Angular Injector used to retrieve service instances.
 * @param componentType - The component object which contains the type information.
 * @returns An instance of PageComponentService corresponding to the type of the Page Component or Layout Component.
 */
export function createBuilderComponentFactory(
  injector: Injector,
  componentType: PageLayoutType | HeroBannerPageComponentType | DefaultPageComponentType,
): PageComponentService {
  if (isHeroBannerPageComponentType(componentType)) {
    return injector.get(HeroBannerPageComponentService);
  }

  if (isPageLayoutType(componentType)) {
    return injector.get(PageLayoutComponentService);
  }

  return injector.get(DefaultPageComponentService);
}
